import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import RadioGroup from "components/DesignSystem/RadioGroup/RadioGroup";
import { useState } from "react";
import { Product, useGetAllProductsQuery } from "store/apis/products";
import { FOREIGN_QUALIFICATION } from "types/Models/services";
import {
  FOREIGN_QUALIFICATION_PRICING_TIER_OPTIONS,
  FOREIGN_QUALIFICATION_PRICING_TIER_STANDARD,
  FOREIGN_QUALIFICATION_PRICING_TIER_TURNAROUND,
  ForeignQualificationPricingTier,
} from "constants/foreignQualificationPricingTier";

type ForeignQualificationTierModalProps = {
  open: boolean;
  groupId: string;
  entityId: string;
  stateId: string;
  stateName: string;
  isConfirming?: boolean;
  onConfirm: (tier: ForeignQualificationPricingTier) => void;
  onClose: () => void;
};

export const ForeignQualificationTierModal = ({
  open,
  groupId,
  entityId,
  stateId,
  stateName,
  isConfirming = false,
  onConfirm,
  onClose,
}: ForeignQualificationTierModalProps) => {
  const [tier, setTier] = useState<ForeignQualificationPricingTier>(
    FOREIGN_QUALIFICATION_PRICING_TIER_STANDARD
  );

  const { data: products } = useGetAllProductsQuery(
    { groupId, entityId, search: "Foreign Qualification" },
    { skip: !groupId || !entityId }
  );

  const allProducts = products
    ? Object.values(products).flatMap((productGroup) =>
        Object.values(productGroup).flatMap((value) =>
          Array.isArray(value) && value.length > 0
            ? (value.filter(
                (item) => !("subscription_name" in item)
              ) as Product[])
            : []
        )
      )
    : [];

  const foreignQualificationProduct = allProducts.find(
    (product) => product.base_task_key === FOREIGN_QUALIFICATION
  );

  const stateInfo = foreignQualificationProduct?.states?.find(
    (state) => state.state_id === stateId
  );

  const priceForTier = (
    optionTier: ForeignQualificationPricingTier
  ): string | null | undefined =>
    optionTier === FOREIGN_QUALIFICATION_PRICING_TIER_STANDARD
      ? stateInfo?.price
      : stateInfo?.priority_price;

  const handleConfirm = () => {
    onConfirm(tier);
  };

  return (
    <Modal.Root open={open} onOpenChange={onClose}>
      <Modal.Content useCustomOverlay>
        <Modal.Header>
          <Modal.Title>Choose a pricing tier</Modal.Title>
          <Modal.Close />
        </Modal.Header>
        <Modal.Body className="t-flex t-flex-col t-gap-4">
          <span className="t-text-body-sm t-text-text-30">
            Choose Foreign Qualification pricing for {stateName}
          </span>
          <RadioGroup.Root
            value={tier}
            onValueChange={(value) =>
              setTier(value as ForeignQualificationPricingTier)
            }
          >
            <RadioGroup.Content>
              {FOREIGN_QUALIFICATION_PRICING_TIER_OPTIONS.map((option) => {
                const optionTier =
                  option.value as ForeignQualificationPricingTier;
                const price = priceForTier(optionTier);
                const title =
                  price != null ? `${option.label} — $${price}` : option.label;

                return (
                  <RadioGroup.Item
                    key={option.value}
                    value={option.value}
                    description={
                      FOREIGN_QUALIFICATION_PRICING_TIER_TURNAROUND[optionTier]
                    }
                  >
                    {title}
                  </RadioGroup.Item>
                );
              })}
            </RadioGroup.Content>
          </RadioGroup.Root>
        </Modal.Body>
        <Modal.FooterButtonGroup>
          <Button
            onClick={onClose}
            customType="secondary"
            type="button"
            disabled={isConfirming}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            customType="primary"
            type="button"
            isLoading={isConfirming}
            disabled={isConfirming}
          >
            Continue
          </Button>
        </Modal.FooterButtonGroup>
      </Modal.Content>
    </Modal.Root>
  );
};
